import type { HTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { Icon } from './icons.js';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** `.hp-field` — label above a 38 px control. */
export function HpField({
  label,
  children,
  className,
  hint,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
  hint?: ReactNode;
}) {
  return (
    <label className={cx('hp-field', className)}>
      <span>{label}</span>
      {children}
      {hint && <small className="bo-field-hint">{hint}</small>}
    </label>
  );
}

/** `.form-grid` — two columns, 18 px gap; textareas are `.full`. */
export function FormGrid({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('form-grid', className)} {...rest}>
      {children}
    </div>
  );
}

export interface FormFieldProps {
  label: ReactNode;
  /** Spans both columns. */
  full?: boolean;
  /** Helper text under the control (11 px). */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** `.form-field(.full)` — label, control, optional hint. */
export function FormField({ label, full, hint, children, className }: FormFieldProps) {
  return (
    <label className={cx('form-field', full && 'full', className)}>
      <span className="bo-form-label">{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

/** `.checkbox-line` — checkbox with its sentence. */
export function CheckboxLine({
  children,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { children: ReactNode }) {
  return (
    <label className={cx('checkbox-line', className)}>
      <input type="checkbox" {...rest} />
      <span>{children}</span>
    </label>
  );
}

/** `.form-errors[role=alert]` — the one error block at the top of a form. */
export function FormErrors({ children, id }: { children: ReactNode; id?: string }) {
  if (!children) return null;
  return (
    <div id={id} className="form-errors" role="alert">
      {children}
    </div>
  );
}

/** `.toolbar` — search field + selects + trailing actions above a table. */
export function Toolbar({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('toolbar', className)} {...rest}>
      {children}
    </div>
  );
}

export function ToolbarSpacer() {
  return <div className="toolbar-spacer" />;
}

/** `.search-field` — magnifier + input. */
export function SearchField({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={cx('search-field', className)}>
      <Icon name="search" />
      <input type="search" {...rest} />
    </div>
  );
}

/** `.filter-select` — compact select for list filters. */
export function FilterSelect({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx('filter-select', className)} {...rest}>
      {children}
    </select>
  );
}
